// packages/web-ui/src/api/clients/kb.ts
//
// Story W4-S5 (AC #7) — typed client wrapping the two real KB endpoints
// filled by this story (`/api/v1/kb/entity/:entityId` + `/api/v1/kb/entities`).
// Mirrors the W4-S4 changeset.ts client pattern: `Result<T, ApiError>` envelope
// per CLAUDE.md "Coding Standards"; uses the shared `apiCall` helper for
// bearer-token + X-Request-Id wiring; types come from `@tessa-lean/base`.
//
// Out of scope (per story Pitfall #6): `/api/v1/kb/mentions` (W4-S6),
// `/api/v1/kb/config*` (W6-S2). Those stubs stay 501.

import type { Result } from '@tessa-lean/base';
import type {
  KbEntity,
  KbEntityKind,
  KbEntityListResponse,
  ReactFlowLayoutJson,
} from '@tessa-lean/base/api-schemas/index.js';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

/** Query params for `listEntities` — mirrors `KbEntityListQuerySchema`. */
export interface KbListParams {
  kind?: KbEntityKind;
  parent?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Build the `?kind=&search=&...` query string from params. */
function buildQuery(params: KbListParams): string {
  const parts: string[] = [];
  if (params.kind !== undefined) parts.push(`kind=${encodeURIComponent(params.kind)}`);
  if (params.parent !== undefined && params.parent.length > 0)
    parts.push(`parent=${encodeURIComponent(params.parent)}`);
  if (params.search !== undefined && params.search.length > 0)
    parts.push(`search=${encodeURIComponent(params.search)}`);
  if (params.page !== undefined) parts.push(`page=${String(params.page)}`);
  if (params.pageSize !== undefined) parts.push(`pageSize=${String(params.pageSize)}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/**
 * `GET /api/v1/kb/entities` → `{ items: KbEntity[], nextPage? }`. Filters
 * (`kind`, `parent`, `search`) are server-side; paging defaults to 200/page.
 */
export function listEntities(
  ctx: ApiContext,
  params: KbListParams = {},
): Promise<Result<KbEntityListResponse, ApiError>> {
  return apiCall<undefined, KbEntityListResponse>(
    ctx,
    'GET',
    `/api/v1/kb/entities${buildQuery(params)}`,
  );
}

/**
 * `GET /api/v1/kb/entity/:entityId` → `KbEntity`.
 * `404 KB_ENTITY_NOT_FOUND` when absent; `503 KB_INDEX_REBUILDING` when the
 * server-side index is mid-rebuild — surfaced verbatim via the error envelope.
 */
export function getEntity(
  ctx: ApiContext,
  entityId: string,
): Promise<Result<KbEntity, ApiError>> {
  return apiCall<undefined, KbEntity>(
    ctx,
    'GET',
    `/api/v1/kb/entity/${encodeURIComponent(entityId)}`,
  );
}

/**
 * `GET /api/v1/kb/entity/:entityId/ibd` → `ReactFlowLayoutJson` showing the
 * direct children of `entityId` as an Internal Block Diagram. Mirrors the
 * `getEntity` semantics: 503 KB_INDEX_REBUILDING / 404 KB_ENTITY_NOT_FOUND
 * surface verbatim via the error envelope.
 */
export function getEntityIbd(
  ctx: ApiContext,
  entityId: string,
): Promise<Result<ReactFlowLayoutJson, ApiError>> {
  return apiCall<undefined, ReactFlowLayoutJson>(
    ctx,
    'GET',
    `/api/v1/kb/entity/${encodeURIComponent(entityId)}/ibd`,
  );
}

/**
 * `GET /api/v1/kb/entity/:entityId/context` → `ReactFlowLayoutJson` showing the
 * entity + its one-hop neighbours (the deterministic context interconnection,
 * derived from `buildEntityContextLayout`). Unlike `/ibd` (children), this is
 * useful for a leaf part: it renders the element with its ports + directly
 * connected parts instead of an empty "no child parts" state. Same error
 * semantics as `getEntity` (503 KB_INDEX_REBUILDING / 404 KB_ENTITY_NOT_FOUND).
 */
export function getEntityContext(
  ctx: ApiContext,
  entityId: string,
): Promise<Result<ReactFlowLayoutJson, ApiError>> {
  return apiCall<undefined, ReactFlowLayoutJson>(
    ctx,
    'GET',
    `/api/v1/kb/entity/${encodeURIComponent(entityId)}/context`,
  );
}

/** Convenience barrel for tests + slice consumers. */
export const kbApi = {
  listEntities,
  getEntity,
  getEntityIbd,
  getEntityContext,
};

export type KbApi = typeof kbApi;
